<template>
    <div
        ref="gridParent"
        :style="generalThemeStyles"
        style="width: 100%; height: calc(100vh - 64px - 52px); overflow-y: auto; font-size: 8pt"
        class="scrollbar-hidden"
    >
        <svg
            :id="layout.id"
            ref="svg"
            class="designer-container designer-layout"
            :height="layout.height"
            :width="layout.width"
            :style="{
                transform: `scale(${preview ? transformRatio : 1})`,
                'transform-origin': 'top left',
            }"
        >
            <rect
                x="0"
                y="0"
                width="100%"
                height="100%"
                :fill="!preview ? 'rgba(227, 227, 227, 255)' : 'transparent'"
                :stroke="!preview ? 'whitesmoke' : 'black'"
                stoke-width="0.5"
                :style="{ filter: !preview ? 'none' : 'none' }"
            />
            <g>
                <rect
                    v-for="cell of layout.gridCells"
                    :key="cell.id"
                    :x="cell.x"
                    :y="cell.y"
                    :width="cell.width"
                    :height="cell.height"
                    stroke="whitesmoke"
                    stoke-width="0.5"
                    fill="transparent"
                    style="border-collapse: collapse"
                    :visibility="preview ? 'hidden' : 'visible'"
                />
            </g>
            <g>
                <template v-for="box of layout.children">
                    <BoxComponent
                        :key="box.id"
                        v-if="box.visible"
                        :box="box"
                        :selected="isBoxSelected(box)"
                        :selectedBoxObj="selectedBox"
                        :viewType="preview ? 'PREVIEW' : 'EDITION'"
                        @selected-box="handleSelectedBox($event)"
                        @moved-box="() => {}"
                        ref="boxComponent"
                    />
                </template>
            </g>
        </svg>
    </div>
</template>
<script lang="ts">
import { Component, Vue, Prop, Watch, toNative } from "vue-facing-decorator";
import BoxComponent from "./BoxComponent.vue";
import CompanyStatusBoard from "../CompanyStatusBoard.vue";
import CashFlowPlan from "../dashboard/CashFlowPlan.vue";
import Home from "../Home.vue";
import { Box, GridContainer, Layout, LayoutMode } from "./models";

interface MoveCoordinates {
    screenX?: number;
    screenY?: number;
}

@Component({
    components: {
        CompanyStatusBoard,
        Home,
        CashFlowPlan,
        BoxComponent,
    },
    emits: ["layout", "selected-box"],
})
export class DesignerLayout extends Vue {
    @Prop() editedBox: Box;
    @Prop({ default: "dashboard" }) view: string;
    @Prop({ default: null }) generalTheme: any;
    @Prop({ default: null }) readonly initialLayout: Layout;
    @Prop({ default: "EDITION" }) viewType: "EDITION" | "PREVIEW";

    title = "Layout";
    layout = new Layout(LayoutMode.grid, 800, 400, null, null);

    selectedBox: Box = null;

    transformRatio = 1;

    viewSizeMap = {
        dashboard: { width: 1536, height: 872 },
        page: { width: 828, height: 1170 },
    };

    get viewSize() {
        return this.viewSizeMap?.[this.view];
    }

    get preview() {
        return this.viewType === "PREVIEW";
    }

    isContainer(box: Box) {
        return box instanceof GridContainer;
    }

    duplicateBox(box) {
        console.log("duplicateBox", box);
    }

    @Watch("editedBox")
    watch_editedBox(newVal, oldVal) {
        this.handleSelectedBox(this.editedBox);
    }

    @Watch("view")
    onViewChanged(newVal, oldVal) {
        if (this.viewSize) {
            this.layout.setWidthAndHeight(this.viewSize.width, this.viewSize.height);
        }
    }

    // resize() {
    //     const parent = this.$refs.gridParent
    //     const parentWidth = (parent as any)?.clientWidth
    //     const svg = this.$refs.svg
    //     const svgWidth = (svg as any)?.clientWidth
    //     if (parentWidth && svgWidth) {
    //         this.transformRatio = parentWidth / svgWidth
    //     }
    // }

    isBoxSelected(box) {
        return box ? box.id === this.selectedBoxId : false;
    }

    get selectedBoxId() {
        return this.selectedBox?.id;
    }

    moveStart: MoveCoordinates = {};

    snap: boolean = true;

    // get selectedBox(): Box {
    //     return this.layout.getChildById(this.selectedBoxId)
    // }

    selectedResizePoint: "left" | "right" | "top" | "bottom" | "box" | "" = "";

    async beforeMount() {
        this.layout = this.initialLayout ?? new Layout(LayoutMode.grid, 800, 400, null, null);
        if (this.viewSize) {
            this.layout.setWidthAndHeight(this.viewSize.width, this.viewSize.height);
        }
        window.addEventListener("resize", this.resize);
        await this.$nextTick();
        this.resize();
        this.initListeners();
        this.$emit("layout", this.layout);
    }

    beforeDestroy() {
        window.removeEventListener("resize", this.resize);
        this.removeListeners();
    }

    @Watch("preview")
    // @Watch("view")
    resize() {
        const svg = this.$refs.svg as any;
        // if (svg) {
        //     // console.log("svg", svg, svg.clientWidth, svg.clientHeight)
        //     this.layout.setWidthAndHeight(svg.clientWidth, svg.clientHeight)
        // }

        const parent = this.$refs.gridParent;
        const parentWidth = (parent as any)?.clientWidth;
        // const svg = this.$refs.svg
        const svgWidth = (svg as any)?.clientWidth;
        if (parentWidth && svgWidth) {
            this.transformRatio = parentWidth < svgWidth ? parentWidth / svgWidth : 1;
            console.log(
                "transformRatio",
                this.transformRatio,
                "parentWidth",
                parentWidth,
                "svgWidth",
                svgWidth,
            );
        }
    }

    handleSelectedBox(box) {
        if (this.selectedBox) {
            // Unselect previous box
            this.selectedBox.isSelected = false;
        }
        this.selectedBox = box;
        if (this.selectedBox) {
            // Selected box can be null
            this.selectedBox.isSelected = true;
        }
        this.$emit("selected-box", box);
        console.log("handling selected-box", box);
    }

    initListeners() {
        document.addEventListener("click", this.onClick);
    }

    removeListeners() {
        document.removeEventListener("click", this.onClick);
    }

    onClick(evt) {
        if (!evt.target?.closest(".box-group") && evt.target?.closest("#grid")) {
            this.handleSelectedBox(null);
        }
    }

    get generalThemeStyles() {
        const style = { ...(this.generalTheme ?? {}) };
        return {
            fontSize: style.fontSize != null ? style.fontSize + "pt" : null,
            fontFamily: style.fontFamily,
        };
    }

    // initListeners() {
    //     console.log('document.querySelector(".resizePoint")', document.querySelector(".resizePoint"))
    //     // document.querySelectorAll(".resizePoint").forEach(el => el.addEventListener("mousedown", this.onMouseDown))
    //     document.addEventListener("mouseup", this.onMouseUp)
    //     document.addEventListener("mousemove", this.onMouseMove)
    //     // document.querySelectorAll(".box-group").forEach(el => el.addEventListener("mousedown", this.onMouseDownBox))
    //     // document.querySelectorAll(".box-group").forEach(el => el.addEventListener("mouseleave", this.onMouseLeaveBox))

    //     document.addEventListener("mousedown", (evt) => {
    //         const target = (evt.target as any).closest(".resizePoint")
    //         console.log("Closest resize point", target, "evt.target", evt.target)
    //         if (target) {
    //             this.onMouseDown(evt)
    //         }
    //     })

    //     document.addEventListener("mousedown", (evt) => {
    //         const target = (evt.target as any).closest(".box-group")
    //         console.log("Closest box-group", target)
    //         if (target) {
    //             this.onMouseDownBox(evt)
    //         }
    //     })
    // }

    // removeListeners() {
    //     // document.querySelectorAll(".resizePoint").forEach(el => el.removeEventListener("mousedown", this.onMouseDown))
    //     document.removeEventListener("mouseup", this.onMouseUp)
    //     document.removeEventListener("mousemove", this.onMouseMove)
    //     // document.querySelectorAll(".box-group").forEach(el => el.removeEventListener("mousedown", this.onMouseDownBox))
    //     // // document.querySelectorAll(".box-group").forEach(el => el.removeEventListener("mouseleave", this.onMouseLeaveBox))

    //     document.removeEventListener("mousedown", null)
    //     // document.removeEventListener("mousedown", null)
    // }

    // setMoveStart(evt) {
    //     console.log("setMoveStart evt", evt)
    //     if (evt) {
    //         this.moveStart.screenX = evt.screenX
    //         this.moveStart.screenY = evt.screenY
    //     }
    // }

    // onMouseDown(evt) {
    //     evt.preventDefault()
    //     evt.stopPropagation()
    //     evt.stopImmediatePropagation()  // Important since an element and its parent have the same event listener
    //     const parent = evt.target?.parentElement
    //     const boxId = parent.dataset?.["boxId"]
    //     this.selectedBoxId = boxId
    //     const classList = [...(evt.target?.classList ?? [])]
    //     this.setMoveStart(evt)
    //     console.log("classList", classList)
    //     if (classList.includes("left")) {
    //         this.selectedResizePoint = "left"
    //     } else if(classList.includes("top")) {
    //         this.selectedResizePoint = "top"
    //     } else if (classList.includes("right")) {
    //         this.selectedResizePoint = "right"
    //     } else if (classList.includes("bottom")) {
    //         this.selectedResizePoint = "bottom"
    //     }
    //     console.log("HERRRRRE evt", "this.selectedResizePoint", this.selectedResizePoint, evt, "parent", parent, "boxId", boxId, "selectedBox", this.selectedBox)
    // }

    // onMouseUp(evt) {
    //     evt.preventDefault()
    //     this.selectedBoxId = ""
    //     this.selectedResizePoint = ""
    //     console.log("MouseUP", evt)
    //     this.moveStart.screenX = null
    //     this.moveStart.screenY = null
    // }

    // onMouseMove(evt) {
    //     evt.preventDefault()
    //     // console.log("onMouseMove evt", evt,  "id", this.selectedBoxId, evt.movementX)
    //     if (this.selectedBoxId && this.selectedBox) {
    //         console.log("onMouseMove evt", "this.selectedResizePoint", this.selectedResizePoint, evt,  evt.movementX, "selectedBoxId", this.selectedBoxId)

    //         // if (["left", "top"].includes(this.selectedResizePoint)) {
    //         //     this.selectedBox.x += evt.movementX
    //         //     this.selectedBox.y += evt.movementY

    //         //     this.selectedBox.width -= evt.movementX
    //         //     this.selectedBox.height -= evt.movementY
    //         // } else if (["right", "bottom"].includes(this.selectedResizePoint)) {
    //         //     this.selectedBox.width += evt.movementX
    //         //     this.selectedBox.height += evt.movementY
    //         // }

    //         let [movementX, movementY] = this.moveAndReturnOffsets(evt)

    //         // console.log("[movementX, movementY]", [movementX, movementY])

    //         switch(this.selectedResizePoint) {
    //             case "left":
    //                 this.selectedBox.x += movementX
    //                 this.selectedBox.width -= movementX
    //                 break
    //             case "right":
    //                 console.log("right")
    //                 this.selectedBox.width += movementX
    //                 break
    //             case "top":
    //                 this.selectedBox.y += movementY
    //                 this.selectedBox.height -= movementY
    //                 break
    //             case "bottom":
    //                 this.selectedBox.height += movementY
    //                 break
    //             case "box":
    //                 // this.onMouseMoveBox(evt)
    //                 this.selectedBox.x += movementX
    //                 this.selectedBox.y += movementY
    //         }
    //     }
    // }

    // onMouseDownBox(evt) {
    //     evt.preventDefault()
    //     let box = evt.target
    //     this.setMoveStart(evt)
    //     console.log("setMoveStart", this.moveStart)
    //     if (box.tagName !== "g" && ![...box.classList].includes("box-group")) {
    //         box = evt.target?.closest("g.box-group")
    //     }
    //     console.log("box", box)
    //     if (box) {
    //         const boxId = box.dataset?.["boxId"]
    //         this.selectedBoxId = boxId
    //         this.selectedResizePoint = "box"
    //         console.log("evt", evt, "boxId", boxId, "closest g", evt.target.closest("g"))
    //     }
    // }

    // onMouseLeaveBox(evt) {
    //     evt.preventDefault()
    //     this.selectedBoxId = ""
    //     this.selectedResizePoint = ""
    //     // this.moveStart.screenX = null
    //     // this.moveStart.screenY = null
    // }

    // onMouseUpBox(evt) {
    //     evt.preventDefault()
    //     this.selectedBoxId = ""
    //     this.selectedResizePoint = ""
    //     this.moveStart.screenX = null
    //     this.moveStart.screenY = null
    // }

    // moveAndReturnOffsets(evt) {
    //     // console.log("[movementX, movementY] previous", this.moveStart, "current", {screenX: evt.screenX, screenY: evt.screenY})
    //     let movementX = this.moveStart.screenX != null ? evt.screenX - this.moveStart.screenX : 0
    //     let movementY = this.moveStart.screenY != null ? evt.screenY - this.moveStart.screenY : 0
    //     if (this.snap && Math.abs(movementX) < this.layout.gridCellWidth) {
    //         movementX = 0
    //     } else {
    //         this.moveStart.screenX = evt.screenX
    //     }

    //     if (this.snap && Math.abs(movementY) < this.layout.gridCellHeight) {
    //         movementY = 0
    //     } else {
    //         this.moveStart.screenY = evt.screenY
    //     }

    //     return [movementX, movementY]
    // }
}

export default toNative(DesignerLayout);
</script>
<style lang="css" scoped>
/* .box:hover {
    stroke: #F9B128;
    stroke-width: 2;
} */
/* .resizePoint {
    display: none;
    cursor: default;
}

.box-group:hover {
    cursor: move;
}

.box-group:hover .box {
    stroke: #f9b128;
    stroke-width: 2;
}

.box-group:hover .resizePoint {
    display: inline;
}

.box-group:hover .resizePoint.left,
.box-group:hover .resizePoint.right {
    cursor: ew-resize;
}

.box-group:hover .resizePoint.bottom,
.box-group:hover .resizePoint.top {
    cursor: ns-resize;
} */

.scrollbar-hidden::-webkit-scrollbar {
    overflow: auto;
    display: none;
    width: 12px;
}

.scrollbar-hidden {
    overflow: auto;
    -ms-overflow-style: none;
    scrollbar-width: none;
}
</style>
